import React, { memo, useRef } from 'react';

/**
 * Composant d'alerte de pluie
 * Affiche une alerte lorsqu'il y a un risque de pluie dans l'heure
 * 
 * @param {Object} props - Propriétés du composant
 * @param {Object} props.rainData - Données de pluie
 * @param {string} props.language - Langue actuelle ('fr' ou 'en')
 */
export const RainAlert = memo(({ rainData, language }) => {
  const nodeRef = useRef(null);
  
  if (!rainData || !rainData.willRain) return null;

  const translations = {
    fr: {
      title: "⚠️ Risque de pluie dans l'heure",
      titleSnow: "⚠️ Risque de neige dans l'heure",
      description: "Pluie détectée dans l'heure à venir.",
      descriptionSnow: "Chute de neige détectée dans l'heure à venir.",
      light: "Pluie légère",
      lightSnow: "Neige légère",
      moderate: "Pluie modérée",
      moderateSnow: "Neige modérée",
      heavy: "Pluie forte",
      heavySnow: "Neige forte",
      maxMm: "Jusqu'à",
      maxProb: "Probabilité",
      source: {
        meteofrance: "Source: Météo-France",
        openmeteo: "Source: Open-Meteo"
      }
    },
    en: {
      title: "⚠️ Rain risk within the hour",
      titleSnow: "⚠️ Snow risk within the hour",
      description: "Rain detected in the next hour.",
      descriptionSnow: "Snowfall detected in the next hour.",
      light: "Light rain",
      lightSnow: "Light snow",
      moderate: "Moderate rain",
      moderateSnow: "Moderate snow",
      heavy: "Heavy rain",
      heavySnow: "Heavy snow",
      maxMm: "Up to",
      maxProb: "Probability",
      source: {
        meteofrance: "Source: Météo-France",
        openmeteo: "Source: Open-Meteo"
      }
    }
  };

  const t = translations[language] || translations.fr;
  const isSnow = rainData.isSnow;

  // Intensité basée sur la quantité MOYENNE sur l'heure
  let intensityKey = 'light';
  if (rainData.amount > 5) {
    intensityKey = 'heavy';
  } else if (rainData.amount > 2) {
    intensityKey = 'moderate';
  }
  
  if (isSnow) {
    intensityKey += 'Snow';
  }

  return (
    <div ref={nodeRef} className="rain-alert" role="alert" aria-live="polite">
      <div className="rain-alert-title">{isSnow ? t.titleSnow : t.title}</div>
      <div>{isSnow ? t.descriptionSnow : t.description}</div>
      <div className="rain-alert-details">
        <span className="rain-intensity">{t[intensityKey]}</span>
        <span className="rain-amount">{t.maxMm} {rainData.amountMax.toFixed(1)} mm</span>
        <span className="rain-probability">{t.maxProb} : {rainData.probabilityMax.toFixed(0)}%</span>
        <span className="rain-source">{t.source[rainData.source || 'openmeteo']}</span>
      </div>
    </div>
  );
});

RainAlert.displayName = 'RainAlert';

/**
 * Composant de message d'aide pour la géolocalisation
 * Affiché lorsque la géolocalisation est refusée
 * 
 * @param {Object} props - Propriétés du composant
 * @param {boolean} props.show - Indique si le message doit être affiché
 * @param {string} props.language - Langue actuelle ('fr' ou 'en')
 */
export const GeolocationHelp = memo(({ show, language }) => {
  if (!show) return null;

  const message = language === 'fr' 
    ? "Cliquez sur 📍 Ma position pour activer la météo locale."
    : "Click on 📍 My location to enable local weather.";

  return (
    <div className="geolocation-help" role="alert">
      <div className="geolocation-help-message">{message}</div>
    </div>
  );
});

GeolocationHelp.displayName = 'GeolocationHelp';

export default RainAlert;
